import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    ReactNode,
    useCallback
} from "react";
import { useAuth } from "../app/modules/auth";

// Interfaces
export interface Organization {
    OrganizationId: number;
    OrganizationName: string;
    Idcorporacao: number;
    CorporationName: string;
}

export interface Corporation {
    id: number;
    name: string;
}

export interface FiltrosGlobais {
    selectedCorporation: number | null;
    selectedOrganizations: string[];
    availableOrganizations: Organization[];
    availableCorporations: Corporation[];
}

interface FiltrosContextProps {
    // Estado atual dos filtros
    filtrosGlobais: FiltrosGlobais;

    // Funções para atualizar filtros
    setSelectedCorporation: (corpId: number | null) => void;
    setSelectedOrganizations: (orgs: string[]) => void;
    setAvailableOrganizations: (orgs: Organization[]) => void;
    setAvailableCorporations: (corps: Corporation[]) => void;

    // Funções utilitárias
    clearFiltros: () => void;
    loadUserOrganizations: (userId: string) => Promise<boolean>;
    isInitialized: boolean;
}

const FiltrosContext = createContext<FiltrosContextProps | undefined>(undefined);

export const useFiltrosGlobais = () => {
    const context = useContext(FiltrosContext);
    if (!context) {
        throw new Error("useFiltrosGlobais deve ser usado dentro de um FiltrosProvider");
    }
    return context;
};

interface FiltrosProviderProps {
    children: ReactNode;
}

const STORAGE_KEY = 'filtros_globais_v1';

export const FiltrosProvider: React.FC<FiltrosProviderProps> = ({ children }) => {
    const { auth } = useAuth();
    const backendUrl = process.env.REACT_APP_BACKEND_URL;

    const [filtrosGlobais, setFiltrosGlobais] = useState<FiltrosGlobais>({
        selectedCorporation: null,
        selectedOrganizations: [],
        availableOrganizations: [],
        availableCorporations: []
    });

    const [isInitialized, setIsInitialized] = useState(false);

    // Função para salvar no localStorage
    const saveFiltrosToStorage = useCallback((filtros: Partial<FiltrosGlobais>, userId?: string) => {
        try {
            const currentData = localStorage.getItem(STORAGE_KEY);
            const existingData = currentData ? JSON.parse(currentData) : {};

            const newData = {
                ...existingData,
                ...filtros,
                userId: userId || auth?.User?.Email, // Salvar o usuário associado aos filtros
                timestamp: Date.now()
            };

            localStorage.setItem(STORAGE_KEY, JSON.stringify(newData));
            console.log("💾 Filtros salvos no localStorage:", newData);
        } catch (error) {
            console.error("❌ Erro ao salvar filtros:", error);
        }
    }, [auth?.User?.Email]);

    // Função para carregar do localStorage
    const loadFiltrosFromStorage = useCallback((): Partial<FiltrosGlobais> | null => {
        try {
            const data = localStorage.getItem(STORAGE_KEY);
            if (!data) return null;

            const parsedData = JSON.parse(data);

            // Verificar se os filtros pertencem ao usuário atual
            if (parsedData.userId && parsedData.userId !== auth?.User?.Email) {
                console.log("🔄 Filtros de outro usuário detectados, limpando localStorage");
                localStorage.removeItem(STORAGE_KEY);
                return null;
            }

            // Verificar se os dados não são muito antigos (24 horas)
            const TWENTY_FOUR_HOURS = 24 * 60 * 60 * 1000;
            if (parsedData.timestamp && Date.now() - parsedData.timestamp > TWENTY_FOUR_HOURS) {
                console.log("🕐 Filtros expirados, limpando localStorage");
                localStorage.removeItem(STORAGE_KEY);
                return null;
            }

            console.log("📂 Filtros carregados do localStorage:", parsedData);
            return parsedData;
        } catch (error) {
            console.error("❌ Erro ao carregar filtros:", error);
            return null;
        }
    }, [auth?.User?.Email]);

    // Função para buscar organizações do usuário
    const loadUserOrganizations = useCallback(async (userId: string): Promise<boolean> => {
        if (!userId || !backendUrl) {
            console.log("❌ UserId ou backendUrl não disponível", { userId, backendUrl });
            return false;
        }

        try {
            console.log("🔄 Carregando organizações do usuário:", userId);

            const url = `${backendUrl}/api/UserOrganization/CorpByUser?userId=${userId}`;
            console.log("📡 URL das organizações:", url);

            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${auth?.Token}`
                }
            });

            console.log("📥 Resposta das organizações - Status:", response.status);

            if (!response.ok) {
                console.error(`❌ Erro HTTP ${response.status}:`, response.statusText);
                throw new Error(`HTTP ${response.status}`);
            }

            const data = await response.json();

            if (!Array.isArray(data) || data.length === 0) {
                console.log("❌ Nenhuma organização encontrada");
                return false;
            }

            // Processar corporações únicas
            const corporationsMap = new Map();
            data.forEach(org => {
                const key = `${org.Idcorporacao}-${org.CorporationName}`;
                if (!corporationsMap.has(key)) {
                    corporationsMap.set(key, {
                        id: org.Idcorporacao,
                        name: org.CorporationName
                    });
                }
            });

            const corporations = Array.from(corporationsMap.values())
                .sort((a, b) => {
                    // Ordenar por nome alfabeticamente para garantir consistência
                    return a.name.localeCompare(b.name);
                });
            
            console.log("📊 Corporações encontradas (ordenadas):", corporations.map(c => `${c.id}-${c.name}`));

            // Atualizar dados disponíveis
            setFiltrosGlobais(prev => ({
                ...prev,
                availableOrganizations: data,
                availableCorporations: corporations
            }));

            // SEMPRE inicializar com a primeira corporação para garantir dados do usuário atual
            // Não restaurar filtros salvos para evitar conflitos entre usuários
            const firstCorp = corporations[0];
            console.log("🎯 Inicializando com primeira corporação (sem usar cache):", {
                id: firstCorp?.id,
                name: firstCorp?.name,
                totalCorps: corporations.length
            });
            
            if (firstCorp) {
                const firstCorpOrgs = data
                    .filter(org => org.Idcorporacao === firstCorp.id)
                    .map(org => org.OrganizationName);

                console.log("🎯 Selecionando primeira corporação:", {
                    corpId: firstCorp.id,
                    corpName: firstCorp.name,
                    organizacoes: firstCorpOrgs
                });

                setFiltrosGlobais(prev => ({
                    ...prev,
                    selectedCorporation: firstCorp.id,
                    selectedOrganizations: firstCorpOrgs,
                    availableOrganizations: data,
                    availableCorporations: corporations
                }));

                // Salvar seleção inicial com o userId para futuras sessões do mesmo usuário
                saveFiltrosToStorage({
                    selectedCorporation: firstCorp.id,
                    selectedOrganizations: firstCorpOrgs
                });
                
                console.log("💾 Filtros iniciais salvos no localStorage para o usuário atual");
            }

            setIsInitialized(true);
            return true;

        } catch (error) {
            console.error("❌ Erro ao carregar organizações:", error);
            setIsInitialized(true);
            return false;
        }
    }, [backendUrl, auth?.Token, loadFiltrosFromStorage, saveFiltrosToStorage]);

    // Funções para atualizar estado
    const setSelectedCorporation = useCallback((corpId: number | null) => {
        console.log("🏢 Alterando corporação para:", corpId);

        setFiltrosGlobais(prev => {
            const newState = { ...prev, selectedCorporation: corpId };

            if (corpId) {
                // Selecionar todas organizações da corporação
                const orgsFromCorp = prev.availableOrganizations
                    .filter(org => org.Idcorporacao === corpId)
                    .map(org => org.OrganizationName);

                newState.selectedOrganizations = orgsFromCorp;

                console.log("📋 Organizações da corporação:", orgsFromCorp);

                // Salvar no localStorage
                saveFiltrosToStorage({
                    selectedCorporation: corpId,
                    selectedOrganizations: orgsFromCorp
                });
            } else {
                newState.selectedOrganizations = [];

                // Limpar localStorage
                saveFiltrosToStorage({
                    selectedCorporation: null,
                    selectedOrganizations: []
                });
            }

            return newState;
        });
    }, [saveFiltrosToStorage]);

    const setSelectedOrganizations = useCallback((orgs: string[]) => {
        console.log("📋 Alterando organizações para:", orgs);

        setFiltrosGlobais(prev => {
            const newState = { ...prev, selectedOrganizations: orgs };

            // Verificar se todas organizações pertencem à mesma corporação
            if (orgs.length > 0) {
                const selectedOrgData = prev.availableOrganizations
                    .filter(org => orgs.includes(org.OrganizationName));

                const uniqueCorps = Array.from(new Set(selectedOrgData.map(org => org.Idcorporacao)));

                if (uniqueCorps.length === 1) {
                    newState.selectedCorporation = uniqueCorps[0];
                } else {
                    newState.selectedCorporation = null;
                }
            } else {
                newState.selectedCorporation = null;
            }

            // Salvar no localStorage
            saveFiltrosToStorage({
                selectedCorporation: newState.selectedCorporation,
                selectedOrganizations: orgs
            });

            return newState;
        });
    }, [saveFiltrosToStorage]);

    const setAvailableOrganizations = useCallback((orgs: Organization[]) => {
        setFiltrosGlobais(prev => ({ ...prev, availableOrganizations: orgs }));
    }, []);

    const setAvailableCorporations = useCallback((corps: Corporation[]) => {
        setFiltrosGlobais(prev => ({ ...prev, availableCorporations: corps }));
    }, []);

    const clearFiltros = useCallback(() => {
        console.log("🧹 Limpando todos os filtros");

        setFiltrosGlobais(prev => ({
            ...prev,
            selectedCorporation: null,
            selectedOrganizations: []
        }));

        // Limpar localStorage
        localStorage.removeItem(STORAGE_KEY);
        console.log("✅ Cache limpo com sucesso!");
    }, []);

    // Expor função global para limpar cache via console do browser
    React.useEffect(() => {
        (window as any).clearFiltrosCache = () => {
            console.log("🧹 Limpando cache dos filtros globais...");
            localStorage.removeItem(STORAGE_KEY);
            console.log("✅ Cache limpo! Recarregue a página para aplicar as mudanças.");
            return "Cache limpo com sucesso! Recarregue a página.";
        };
        console.log("💡 Dica: Use clearFiltrosCache() no console para limpar o cache dos filtros");
    }, []);

    // Inicialização automática quando auth muda
    useEffect(() => {
        const initializeFiltros = async () => {
            if (!auth?.User?.Email || isInitialized) return;

            console.log("🚀 Inicializando filtros globais para:", auth.User.Email);
            console.log("🔧 Backend URL configurada:", backendUrl);

            // Verificar se backendUrl está definida
            if (!backendUrl) {
                console.error("❌ REACT_APP_BACKEND_URL não está definida!");
                setIsInitialized(true);
                return;
            }

            try {
                const userUrl = `${backendUrl}/api/User/ByEmail/${encodeURIComponent(auth.User.Email)}`;
                console.log("📡 Buscando usuário na URL:", userUrl);

                // Buscar userId primeiro (igual ao MenuVisaoGeral.tsx)
                const userResponse = await fetch(userUrl);

                console.log("📥 Resposta do usuário - Status:", userResponse.status);

                if (userResponse.ok) {
                    const userId = await userResponse.json();
                    console.log("👤 ID do usuário obtido:", userId);

                    if (userId) {
                        await loadUserOrganizations(userId.toString());
                    } else {
                        console.log("❌ UserId não encontrado nos dados");
                        setIsInitialized(true);
                    }
                } else {
                    console.error("❌ Erro ao buscar usuário:", userResponse.status, userResponse.statusText);
                    setIsInitialized(true);
                }
            } catch (error) {
                console.error("❌ Erro na inicialização dos filtros:", error);
                setIsInitialized(true);
            }
        };

        initializeFiltros();
    }, [auth?.User?.Email, auth?.Token, backendUrl, isInitialized, loadUserOrganizations]);

    const contextValue: FiltrosContextProps = {
        filtrosGlobais,
        setSelectedCorporation,
        setSelectedOrganizations,
        setAvailableOrganizations,
        setAvailableCorporations,
        clearFiltros,
        loadUserOrganizations,
        isInitialized
    };

    return (
        <FiltrosContext.Provider value={contextValue}>
            {children}
        </FiltrosContext.Provider>
    );
}; 